import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { yen, PAYMENTS } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Banknote, QrCode, CreditCard, Landmark } from "lucide-react";

const icons = {
  cash: Banknote,
  paypay: QrCode,
  credit_card: CreditCard,
  japan_post_bank: Landmark,
};

export const PaymentDialog = ({ open, onOpenChange, total, onConfirm, busy }) => {
  const { t } = useI18n();
  const [method, setMethod] = useState("cash");
  const [paid, setPaid] = useState("");

  useEffect(() => {
    if (open) {
      setMethod("cash");
      setPaid("");
    }
  }, [open]);

  const paidNum = Number(paid) || 0;
  const change = method === "cash" ? Math.max(paidNum - total, 0) : 0;
  const insufficient = method === "cash" && paid !== "" && paidNum < total;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("payment")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4 text-center">
            <p className="text-xs text-muted-foreground">{t("total_due")}</p>
            <p className="num text-4xl font-semibold" data-testid="payment-total">
              {yen(total)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {PAYMENTS.map((p) => {
              const Icon = icons[p.id];
              const active = method === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`payment-method-${p.id}`}
                  onClick={() => setMethod(p.id)}
                  className={`flex items-center gap-2 p-3 rounded-xl border text-sm font-medium transition-all duration-200 active:scale-95 ${
                    active
                      ? "border-transparent text-white shadow-sm"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                  style={active ? { backgroundColor: p.color } : {}}
                >
                  <Icon size={18} />
                  {t(`pm_${p.id}`)}
                </button>
              );
            })}
          </div>
          {method === "cash" && (
            <div className="space-y-2">
              <Input
                data-testid="paid-amount-input"
                inputMode="numeric"
                placeholder={t("cash_received")}
                value={paid}
                onChange={(e) => setPaid(e.target.value.replace(/[^0-9]/g, ""))}
                className="h-12 text-lg num"
              />
              <div className="flex gap-2 flex-wrap">
                {[total, 1000, 5000, 10000].map((v, i) => (
                  <Button
                    key={`cash-${v}-${i}`}
                    variant="outline"
                    size="sm"
                    data-testid={`quick-cash-${i}`}
                    onClick={() => setPaid(String(Math.round(v)))}
                  >
                    {yen(v)}
                  </Button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                {t("change")}:{" "}
                <span className="num font-semibold text-foreground" data-testid="change-amount">
                  {yen(change)}
                </span>
              </p>
            </div>
          )}
          <Button
            data-testid="confirm-payment-btn"
            disabled={busy || insufficient}
            onClick={() => onConfirm(method, paidNum)}
            className="w-full h-14 text-base rounded-xl"
          >
            {insufficient ? t("insufficient") : t("finish_payment")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
